// ShapeForm.jsx
import { useState } from "react"
import { UniversalDropDown } from "@fileops/swmm-react"
import "../../css/DemoCode.css"

/**
 * Shape Form is a group of data inputs for entering a cross-section shape for a conduit.
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {string} shapeType The type of shape for the cross-section.
 * @param {string} target The name of the conduit targetted by this form.
 * @returns UI for interactive swmm modeling.
 */
export default function ShapeForm({model, setModel, shapeType, target, fullModel}) {
  const sidewallOptions = ['None', 'One', 'Both']
  const [geom1,   setGeom1  ] = useState(model.XSECTIONS[target].Geom1)
  const [geom2,   setGeom2  ] = useState(model.XSECTIONS[target].Geom2)
  const [geom3,   setGeom3  ] = useState(model.XSECTIONS[target].Geom3)
  const [geom4,   setGeom4  ] = useState(model.XSECTIONS[target].Geom4)
  const [barrels, setBarrels] = useState(model.XSECTIONS[target].Barrels)
  const [culvert, setCulvert] = useState(model.XSECTIONS[target].Culvert)
  const [curve,   setCurve  ] = useState(model.XSECTIONS[target].Curve)
  const [tsect,   setTsect  ] = useState(model.XSECTIONS[target].Tsect)
  const [street,  setStreet ] = useState(model.XSECTIONS[target].Street)
  const [sidewalls, setSidewalls] = useState(sidewallOptions[model.XSECTIONS[target].Geom3])
  const [shapeCurves, setShapeCurves] = useState(Object.keys(fullModel?.CURVES?.Shape?fullModel?.CURVES?.Shape:{}))

  const updateValue = (val, type) => {
    if(model && model.XSECTIONS){
      setModel(prev => ({
        ...prev,
        XSECTIONS:{
          ...prev.XSECTIONS,
          [target]: {
            ...prev.XSECTIONS[target],
            [type]: val
          }
        }
      }))
    }
  }

  const updateSidewalls = (val) => {
    let newVal = 0
    if(val==='None') newVal = 0
    if(val==='One')  newVal = 1
    if(val==='Both') newVal = 2

    if(model && model.XSECTIONS && newVal !== model.XSECTIONS[target].Geom3){
      setSidewalls(sidewallOptions[newVal])
      setModel(prev => ({
        ...prev,
        XSECTIONS:{
          ...prev.XSECTIONS,
          [target]: {
            ...prev.XSECTIONS[target],
            Geom3: newVal
          }
        }
      }))
    }
  }

  if(model){
    return(
      <>
      {
        <>{(
            [
              {
                shapeType: ['DUMMY'], 
                form:
                  []
              },{
                shapeType: ['CIRCULAR', 'EGG', 'HORSESHOE', 'GOTHIC', 'CATENARY', 'SEMIELLIPTICAL', 'BASKETHANDLE', 'SEMICIRCULAR'], 
                form:
                  [{label:'Number of arrels', value: barrels, set: (e)=>setBarrels(e), varName: 'Barrels'},
                  {label:'Max. Height',        value: geom1,   set: (e)=>setGeom1(e)  , varName: 'Geom1'},]
              },{
                shapeType: ['CUSTOM'], 
                form:
                  [{label:'Number of Barrels', value: barrels, set: (e)=>setBarrels(e), varName: 'Barrels'},
                  {label:'Max. Height',        value: geom1,   set: (e)=>setGeom1(e)  , varName: 'Geom1'},]
              },{
                shapeType: ['TRAPEZOIDAL'], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Bottom Width',       value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},
                {label:'Left Slope',         value: geom3,   set: ()=>setGeom3()  , varName: 'Geom3'},
                {label:'Right Slope',        value: geom4,   set: ()=>setGeom4()  , varName: 'Geom4'},]
              },{
                shapeType: ['RECT_OPEN', 'RECT_CLOSED'], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Bottom Width',       value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},]
              },{
                shapeType: ['TRIANGULAR', 'PARABOLIC'], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Top Width',          value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},]
              },{
                shapeType: ['POWER'], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Top Width',          value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},
                {label:'Power',              value: geom3,   set: ()=>setGeom3()  , varName: 'Geom3'},]
              },{
                shapeType: ['FORCE_MAIN'], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Roughness',          value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},]
              },{
                shapeType: ['FILLED_CIRCULAR'], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Filled Depth',       value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},]
              },{
                shapeType: ['HORIZ_ELLIPSE', 'VERT_ELLIPSE', 'ARCH', ], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Maximum Width',      value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},]
              },{
                shapeType: ['RECT_TRIANGULAR', ], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Top Width',          value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},
                {label:'Triangle Height',    value: geom3,   set: ()=>setGeom3()  , varName: 'Geom3'},]
              },{
                shapeType: ['RECT_ROUND', ], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Top Width',          value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},
                {label:'Bottom Radius',      value: geom3,   set: ()=>setGeom3()  , varName: 'Geom3'},]
              },{
                shapeType: ['MODBASKETHANDLE', ], 
                form:
                [{label:'Number of Barrels', value: barrels, set: ()=>setBarrels(), varName: 'Barrels'},
                {label:'Max. Height',        value: geom1,   set: ()=>setGeom1()  , varName: 'Geom1'},
                {label:'Bottom Width',       value: geom2,   set: ()=>setGeom2()  , varName: 'Geom2'},
                {label:'Top Radius',         value: geom3,   set: ()=>setGeom3()  , varName: 'Geom3'},]
              }
            ].map(group=>(
              <div key={group.shapeType}>
              {(
                target && 
                target.length > 0 && 
                (group.shapeType.includes(shapeType)) &&
                <>
                  {
                    group.form.map((v)=>(
                      <label className='editor-small' key={v.varName+'_'+target}>{v.label}
                        <input  type = "text"
                                style={{width: '95%', display: 'flex'}}
                                onChange={(e)=>{v.set(e.target.value); updateValue(e.target.value, v.varName)}}
                                value={v.value}
                                id={v.varName+'_'+target}
                        />
                      </label>
                    ))
                  }
                  {(
                    (group.shapeType.includes('RECT_OPEN')) &&
                    <label className='editor-small' >Sidewalls Removed
                      <UniversalDropDown 
                        IDs={sidewallOptions} 
                        selectText={"Select"} 
                        value={sidewalls}
                        onChange={(val)=>{updateSidewalls(val)}} >
                      </UniversalDropDown>
                    </label>
                  )}
                  {(
                    (group.shapeType.includes('CUSTOM')) &&
                    <label className='editor-small' >Shape Curve Name
                      <UniversalDropDown 
                        IDs={shapeCurves} 
                        selectText={"Select"} 
                        value={curve}
                        onChange={(val)=>{updateValue(val, 'Curve')}} >
                      </UniversalDropDown>
                    </label>
                  )}
                </>
                
              )}
              </div>
            ))
          )}
          {(
            target && 
            target.length > 0 && 
            (shapeType === 'IRREGULAR') &&
            <>
            <label className='editor-small' >Tsect
              <input  type = "text"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setTsect(e.target.value); updateValue(e.target.value, 'Tsect')}}
                      value={tsect}
                      id={'Tsect'+target}
              />
            </label>
            </>
          )}
        </>
      }
      </>
    )
  } else {
    return ( 
    <>
    </>)
  }
}